/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/require-default-props */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import Modal from '../../../../components/Modal/Modal';
import {
   listCommissionTable,
   createCommissionEntry,
} from '../../../../services/routing/routingApi';

// =============================================================================
// Modal de novo lançamento de comissão.
//
// Fluxo:
//   1. Usuário pode escolher uma linha da tabela mestre (autocomplete)
//      → preenche service_key, service_description e unit_value sugerido
//   2. Ou pode digitar tudo manualmente (entrada livre)
//   3. total_value NUNCA é editável no front — backend calcula
// =============================================================================

const Body = styled.div`
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
`;

const FormRow = styled.div`
   display: grid;
   grid-template-columns: ${({ $cols }) => `repeat(${$cols ?? 1}, 1fr)`};
   gap: 12px;
   margin-bottom: 12px;

   @media (max-width: 480px) {
      grid-template-columns: 1fr;
   }
`;

const FieldGroup = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;

   label {
      font-size: 0.7rem;
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
      letter-spacing: 0.3px;
      text-transform: uppercase;
   }

   input, select, textarea {
      padding: 8px 10px;
      font-size: 0.85rem;
      border-radius: 8px;
      border: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
   }
`;

const TotalPreview = styled.div`
   font-size: 0.85rem;
   padding: 10px 12px;
   border-radius: 8px;
   background: ${({ theme }) => theme?.colors?.primarySoft ?? 'rgba(0,0,0,0.04)'};
   margin-bottom: 12px;

   strong {
      font-size: 1rem;
      font-weight: 700;
   }
`;

const HintText = styled.div`
   font-size: 0.7rem;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
   margin-top: 2px;
`;

const FieldError = styled.div`
   font-size: 0.7rem;
   color: #c62828;
   margin-top: 2px;
`;

const ButtonRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 6px;
`;

function formatCurrency(value) {
   const numeric = Number(value);
   if (Number.isNaN(numeric)) return 'R$ 0,00';
   return numeric.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

const EMPTY_FORM = {
   commissionTableId: '',
   serviceKey: '',
   serviceDescription: '',
   quantity: '1',
   unitValue: '',
   notes: '',
};

// =============================================================================

export default function NewCommissionModal({
   isOpen,
   onClose,
   itemId,
   onCommissionCreated,
}) {
   const [tableRows, setTableRows] = useState([]);
   const [isLoadingTable, setIsLoadingTable] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [formData, setFormData] = useState(EMPTY_FORM);
   const [fieldErrors, setFieldErrors] = useState({});

   const formDataRef = useRef(formData);
   useEffect(() => {
      formDataRef.current = formData;
   }, [formData]);

   // Reset ao abrir
   useEffect(() => {
      if (isOpen) {
         setFormData(EMPTY_FORM);
         setFieldErrors({});
      }
   }, [isOpen]);

   // Carrega a tabela mestre uma vez (cacheada na sessão do modal)
   useEffect(() => {
      if (!isOpen) return;
      let cancelled = false;
      setIsLoadingTable(true);
      listCommissionTable()
         .then((rows) => {
            if (!cancelled) setTableRows(rows);
         })
         .catch(() => {
            if (!cancelled) setTableRows([]);
         })
         .finally(() => {
            if (!cancelled) setIsLoadingTable(false);
         });
      return () => {
         cancelled = true;
      };
   }, [isOpen]);

   const handleClose = useCallback(() => {
      if (isSubmitting) return;
      onClose?.();
   }, [isSubmitting, onClose]);

   // ─── Selecionar linha da tabela mestre ──────────────────────
   const handleTableRowSelect = (e) => {
      const selectedId = e.target.value;
      if (!selectedId) {
         setFormData((prev) => ({
            ...prev,
            commissionTableId: '',
         }));
         return;
      }

      const row = tableRows.find((r) => String(r.id) === String(selectedId));
      if (!row) return;

      setFormData((prev) => ({
         ...prev,
         commissionTableId: row.id,
         serviceKey: row.serviceCode,
         serviceDescription: row.serviceDescription,
         unitValue: String(row.commissionValuePerUnit ?? ''),
      }));
   };

   const updateField = (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Limpa o erro do campo correspondente conforme o usuário corrige. As
      // chaves do backend são snake_case (service_key, unit_value…); a UI usa
      // camelCase, então traduzimos no momento da limpeza.
      const apiField = {
         serviceKey: 'service_key',
         serviceDescription: 'service_description',
         quantity: 'quantity',
         unitValue: 'unit_value',
      }[field];
      if (apiField) {
         setFieldErrors((prev) => {
            if (!prev[apiField]) return prev;
            const next = { ...prev };
            delete next[apiField];
            return next;
         });
      }
   };

   // ─── Total calculado em tempo real (apenas preview) ─────────
   const totalPreview = useMemo(() => {
      const qty = Number(formData.quantity) || 0;
      const unit = Number(formData.unitValue) || 0;
      return qty * unit;
   }, [formData.quantity, formData.unitValue]);

   // ─── Validação local antes do POST ──────────────────────────
   const isValid = useMemo(() => {
      const data = formData;
      if (!data.serviceKey || !data.serviceDescription) return false;
      const qty = Number(data.quantity);
      const unit = Number(data.unitValue);
      if (!Number.isFinite(qty) || qty <= 0) return false;
      if (!Number.isFinite(unit) || unit <= 0) return false;
      return true;
   }, [formData]);

   // ─── Submit ─────────────────────────────────────────────────
   const handleSubmit = async () => {
      if (isSubmitting || !isValid) return;
      const data = formDataRef.current;

      setIsSubmitting(true);
      setFieldErrors({});
      try {
         const result = await createCommissionEntry(itemId, {
            commission_table_id: data.commissionTableId
               ? Number(data.commissionTableId)
               : null,
            service_key: data.serviceKey.trim().toUpperCase(),
            service_description: data.serviceDescription.trim(),
            quantity: Number(data.quantity),
            unit_value: Number(data.unitValue),
            notes: data.notes.trim() || null,
         });
         toast.success(result.message || 'Lançamento registrado com sucesso.');
         onCommissionCreated?.(result.commissionEntry);
         onClose?.();
      } catch (err) {
         // Validação Gen3 (M3 §5.2): 422 com errors:[{field,message,code}].
         // Mapeia por campo (service_key, service_description, quantity,
         // unit_value) e mostra o resto — inclusive item_not_realized (§5.3) —
         // no toast geral.
         const responseErrors = err?.response?.data?.errors;
         if (Array.isArray(responseErrors) && responseErrors.length > 0) {
            const errorsByField = {};
            responseErrors.forEach((entry) => {
               if (entry?.field) errorsByField[entry.field] = entry.message;
            });
            setFieldErrors(errorsByField);
         }
         const apiMessage =
            err?.response?.data?.message ??
            responseErrors?.[0]?.message ??
            err?.message ??
            'Erro ao registrar lançamento de comissão.';
         toast.error(apiMessage);
      } finally {
         setIsSubmitting(false);
      }
   };

   if (!isOpen) return null;

   return (
      <Modal
         key={isOpen ? 'open' : 'closed'}
         isOpen={isOpen}
         onClose={handleClose}
         title="Novo lançamento de comissão"
         width="540px"
      >
         <Body>
            <FormRow $cols={1}>
               <FieldGroup>
                  <label>Selecionar da tabela (opcional)</label>
                  <select
                     value={formData.commissionTableId}
                     onChange={handleTableRowSelect}
                     disabled={isSubmitting || isLoadingTable}
                  >
                     <option value="">— digitar manualmente —</option>
                     {tableRows.map((row) => (
                        <option key={row.id} value={row.id}>
                           {row.serviceCode} — {row.serviceDescription}
                           {row.applicableRegion ? ` (${row.applicableRegion})` : ''}
                        </option>
                     ))}
                  </select>
                  <HintText>
                     Escolher uma linha da tabela preenche os campos abaixo
                     automaticamente. Você pode editá-los depois.
                  </HintText>
               </FieldGroup>
            </FormRow>

            <FormRow $cols={2}>
               <FieldGroup>
                  <label>Código do serviço *</label>
                  <input
                     type="text"
                     value={formData.serviceKey}
                     onChange={(e) =>
                        updateField('serviceKey', e.target.value.toUpperCase())
                     }
                     placeholder="Ex.: TEFN, ATPI"
                     maxLength={10}
                     disabled={isSubmitting}
                  />
                  {fieldErrors.service_key && (
                     <FieldError>{fieldErrors.service_key}</FieldError>
                  )}
               </FieldGroup>

               <FieldGroup>
                  <label>Descrição *</label>
                  <input
                     type="text"
                     value={formData.serviceDescription}
                     onChange={(e) =>
                        updateField('serviceDescription', e.target.value)
                     }
                     placeholder="Ex.: Troca de filtro"
                     maxLength={150}
                     disabled={isSubmitting}
                  />
                  {fieldErrors.service_description && (
                     <FieldError>{fieldErrors.service_description}</FieldError>
                  )}
               </FieldGroup>
            </FormRow>

            <FormRow $cols={2}>
               <FieldGroup>
                  <label>Quantidade *</label>
                  <input
                     type="number"
                     min="1"
                     step="1"
                     value={formData.quantity}
                     onChange={(e) => updateField('quantity', e.target.value)}
                     disabled={isSubmitting}
                  />
                  {fieldErrors.quantity && (
                     <FieldError>{fieldErrors.quantity}</FieldError>
                  )}
               </FieldGroup>

               <FieldGroup>
                  <label>Valor unitário (R$) *</label>
                  <input
                     type="number"
                     min="0.01"
                     step="0.01"
                     value={formData.unitValue}
                     onChange={(e) => updateField('unitValue', e.target.value)}
                     placeholder="0,00"
                     disabled={isSubmitting}
                  />
                  {fieldErrors.unit_value && (
                     <FieldError>{fieldErrors.unit_value}</FieldError>
                  )}
               </FieldGroup>
            </FormRow>

            <TotalPreview>
               Total calculado: <strong>{formatCurrency(totalPreview)}</strong>
               <HintText>
                  Quantidade × valor unitário. O backend recalcula no salvamento.
               </HintText>
            </TotalPreview>

            <FormRow $cols={1}>
               <FieldGroup>
                  <label>Observações</label>
                  <textarea
                     rows={2}
                     value={formData.notes}
                     onChange={(e) => updateField('notes', e.target.value)}
                     placeholder="Opcional"
                     disabled={isSubmitting}
                  />
               </FieldGroup>
            </FormRow>

            <ButtonRow>
               <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
               >
                  Cancelar
               </button>
               <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isValid}
               >
                  {isSubmitting ? 'Salvando…' : 'Salvar lançamento'}
               </button>
            </ButtonRow>
         </Body>
      </Modal>
   );
}

NewCommissionModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onCommissionCreated: PropTypes.func,
};
